import { bumpAvatarRevision } from './lib/avatarRevision';
import { principalToStableAuthorId, type PrincipalLike } from './lib/appUserId';
import { appConfirm } from './lib/appDialog';

type Principal = PrincipalLike & {
  userRoles?: string[];
};

function parsePrincipal(data: unknown): Principal | null {
  const raw = (data as { clientPrincipal?: unknown })?.clientPrincipal;
  if (raw == null) return null;
  if (Array.isArray(raw)) return (raw[0] as Principal) ?? null;
  return raw as Principal;
}

async function fetchMe(): Promise<Principal | null> {
  const res = await fetch('/.auth/me', { credentials: 'include' });
  if (!res.ok) return null;
  const data: unknown = await res.json();
  return parsePrincipal(data);
}

function logoutHref(): string {
  const back = `${window.location.origin}/index.html`;
  return `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(back)}`;
}

let settingsSaveAlignResizeBound = false;

function alignSettingsSaveButtonToLegend(): void {
  const saveBtn = document.getElementById('acc-screen-save') as HTMLButtonElement | null;
  const panel = document.querySelector('.account-info-panel') as HTMLElement | null;
  const legend = panel?.querySelector('.tax-fieldset-legend') as HTMLElement | null;
  if (!saveBtn || !panel || !legend) return;

  const panelRect = panel.getBoundingClientRect();
  const legendRect = legend.getBoundingClientRect();
  const leftPx = legendRect.right - panelRect.left + 8;
  saveBtn.style.position = 'absolute';
  saveBtn.style.top = '-30px';
  saveBtn.style.left = `${Math.round(leftPx)}px`;
}

type SettingsMsgTone = 'neutral' | 'error' | 'success';

let settingsSaveToastTimer: number | null = null;
let settingsUnsavedChanges = false;
type SettingsProfileCache = {
  principal: Principal | null;
  isAuthed: boolean;
  authorId: string;
  profile: ProfileMeResponse | null;
  screenName: string;
  avatarLabel: string;
  avatarRefreshToken: number;
  avatarExists: boolean | null;
};
let settingsProfileCache: SettingsProfileCache | null = null;

export function hasSettingsUnsavedChanges(): boolean {
  return settingsUnsavedChanges;
}

function setSettingsUnsavedChanges(value: boolean): void {
  settingsUnsavedChanges = value;
}

function showSettingsSaveToast(text: string, tone: SettingsMsgTone = 'success'): void {
  const id = 'settings-save-toast';
  let toast = document.getElementById(id);
  if (!toast) {
    toast = document.createElement('div');
    toast.id = id;
    toast.className = 'settings-save-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = text;
  toast.classList.remove('is-error');
  if (tone === 'error') toast.classList.add('is-error');
  toast.classList.add('is-visible');
  if (settingsSaveToastTimer !== null) {
    window.clearTimeout(settingsSaveToastTimer);
  }
  settingsSaveToastTimer = window.setTimeout(() => {
    toast?.classList.remove('is-visible');
    settingsSaveToastTimer = null;
  }, 1800);
}

function setSettingsMsg(el: HTMLElement | null, text: string, tone: SettingsMsgTone = 'neutral'): void {
  if (!el) return;
  el.textContent = text;
  el.hidden = false;
  el.classList.remove('home-msg--error', 'home-msg--success');
  if (tone === 'error') el.classList.add('home-msg--error');
  if (tone === 'success') el.classList.add('home-msg--success');
}

function clearSettingsMsg(el: HTMLElement | null): void {
  if (!el) return;
  el.textContent = '';
  el.hidden = true;
  el.classList.remove('home-msg--error', 'home-msg--success');
}

function avatarUrl(authorId: string): string {
  return `/api/avatar?authorId=${encodeURIComponent(authorId || '')}`;
}

function syncSettingsCacheAfterScreenNameSave(screenName: string): void {
  if (!settingsProfileCache) return;
  const trimmed = screenName.trim();
  settingsProfileCache = {
    ...settingsProfileCache,
    screenName: trimmed,
    avatarLabel: trimmed || settingsProfileCache.avatarLabel,
    profile: {
      ...(settingsProfileCache.profile ?? {}),
      screenName: trimmed,
      showName: trimmed || settingsProfileCache.profile?.showName
    }
  };
}

function bumpSettingsAvatarRefreshToken(): number {
  const nextToken = Date.now();
  if (settingsProfileCache) {
    settingsProfileCache = { ...settingsProfileCache, avatarRefreshToken: nextToken };
  }
  return nextToken;
}

function setSettingsAvatarExists(exists: boolean): void {
  if (!settingsProfileCache) return;
  settingsProfileCache = { ...settingsProfileCache, avatarExists: exists };
}

async function apiPutAvatar(file: File): Promise<void> {
  const buf = await file.arrayBuffer();
  const res = await fetch('/api/avatar', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: buf
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error || 'アップロードに失敗しました');
}

async function apiDeleteAvatar(): Promise<void> {
  const res = await fetch('/api/avatar', { method: 'DELETE', credentials: 'include' });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error || '削除に失敗しました');
}

type AvatarBinding = {
  hasPendingAvatar: () => boolean;
  hasSavedAvatar: () => boolean;
  savePendingAvatarFromSelection: () => Promise<void>;
  subscribeDirtyChange: (listener: () => void) => void;
  syncDefaultFallbackLabel: (label: string) => void;
};

function bindAvatar(
  authorId: string,
  fallbackLabel: string,
  initialAvatarRefreshToken: number,
  initialAvatarExists: boolean | null
): AvatarBinding {
  const DELETE_ICON_SRC = '/svg/delete_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.svg';
  const REFRESH_ICON_SRC = '/svg/refresh_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.svg';
  const img = document.getElementById('acc-avatar-img') as HTMLImageElement | null;
  const fb = document.getElementById('acc-avatar-fallback');
  const msg = document.getElementById('acc-avatar-msg');
  const fileInput = document.getElementById('acc-avatar-file') as HTMLInputElement | null;
  const fileTriggerBtn = document.getElementById('acc-avatar-file-trigger') as HTMLButtonElement | null;
  const removeBtn = document.getElementById('acc-avatar-remove') as HTMLButtonElement | null;
  const removeIcon = document.getElementById('acc-avatar-remove-icon') as HTMLImageElement | null;
  const pendingNote = document.getElementById('acc-avatar-pending-note');
  const avatarRow = document.getElementById('acc-avatar-row');
  let previewUrl: string | null = null;
  let hasSavedAvatar = false;
  let pendingDelete = false;
  let currentFallbackLabel = fallbackLabel;
  let avatarRefreshToken = initialAvatarRefreshToken;
  const optimisticAvatar = initialAvatarExists === true;
  let dirtyListener: (() => void) | null = null;

  function notifyDirtyChange(): void {
    dirtyListener?.();
  }

  function initialFromLabel(label: string): string {
    const s = String(label || '').trim();
    if (!s) return '?';
    const ch = Array.from(s)[0] || '?';
    return /[a-z]/.test(ch) ? ch.toUpperCase() : ch;
  }

  function showMsg(text: string, tone: SettingsMsgTone = 'error'): void {
    setSettingsMsg(msg, text, tone);
  }

  function clearMsg(): void {
    clearSettingsMsg(msg);
  }

  function revokePreviewUrl(): void {
    if (!previewUrl) return;
    URL.revokeObjectURL(previewUrl);
    previewUrl = null;
  }

  /** ローカルでファイルを選んでプレビュー中のときの見た目（説明文・枠・選び直し・キャンセル） */
  function updatePendingChrome(hasPendingFile: boolean): void {
    const hasPendingChange = hasPendingFile || pendingDelete;
    if (pendingNote) pendingNote.hidden = !hasPendingChange;
    avatarRow?.classList.toggle('is-pending', hasPendingChange);
    if (!removeBtn || !removeIcon) return;
    if (hasPendingChange) {
      removeIcon.src = REFRESH_ICON_SRC;
      removeBtn.classList.remove('is-delete');
      removeBtn.setAttribute('aria-label', 'Undo');
      removeBtn.title = 'Undo';
      removeBtn.hidden = false;
      notifyDirtyChange();
      return;
    }
    removeIcon.src = DELETE_ICON_SRC;
    removeBtn.classList.add('is-delete');
    removeBtn.setAttribute('aria-label', 'Delete');
    removeBtn.title = 'Delete';
    removeBtn.hidden = !hasSavedAvatar;
    notifyDirtyChange();
  }

  function cancelPendingPreview(): void {
    if (fileInput) fileInput.value = '';
    pendingDelete = false;
    revokePreviewUrl();
    refreshImage();
    updatePendingChrome(false);
    clearMsg();
  }

  function showDeletedPreview(): void {
    if (!img) return;
    revokePreviewUrl();
    if (fb) fb.textContent = initialFromLabel(currentFallbackLabel);
    img.hidden = true;
    img.removeAttribute('src');
    fb?.classList.add('is-visible');
  }

  function showPreview(file: File): void {
    if (!img) return;
    revokePreviewUrl();
    previewUrl = URL.createObjectURL(file);
    img.onload = () => {
      img.hidden = false;
      fb?.classList.remove('is-visible');
    };
    img.onerror = () => {
      img.hidden = true;
      img.removeAttribute('src');
      fb?.classList.add('is-visible');
      showMsg('プレビューの表示に失敗しました。', 'error');
    };
    img.hidden = true;
    fb?.classList.add('is-visible');
    img.src = previewUrl;
  }

  function refreshImage(): void {
    if (!img) return;
    revokePreviewUrl();
    if (fb) fb.textContent = initialFromLabel(currentFallbackLabel);
    img.onerror = () => {
      hasSavedAvatar = false;
      setSettingsAvatarExists(false);
      img.hidden = true;
      img.removeAttribute('src');
      fb?.classList.add('is-visible');
      updatePendingChrome(Boolean(fileInput?.files?.[0]));
    };
    img.onload = () => {
      hasSavedAvatar = true;
      setSettingsAvatarExists(true);
      img.hidden = false;
      fb?.classList.remove('is-visible');
      updatePendingChrome(Boolean(fileInput?.files?.[0]));
    };
    if (optimisticAvatar) {
      hasSavedAvatar = true;
      img.hidden = false;
      fb?.classList.remove('is-visible');
    } else {
      img.hidden = true;
      fb?.classList.add('is-visible');
    }
    img.src = `${avatarUrl(authorId)}&_=${avatarRefreshToken}`;
  }

  updatePendingChrome(false);
  refreshImage();

  fileTriggerBtn?.addEventListener('click', () => {
    fileInput?.click();
  });

  fileInput?.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) {
      pendingDelete = false;
      clearMsg();
      showPreview(f);
      updatePendingChrome(true);
      return;
    }
    refreshImage();
    updatePendingChrome(false);
  });

  removeBtn?.addEventListener('click', async () => {
    const hasPendingFile = Boolean(fileInput?.files?.[0]);
    if (hasPendingFile || pendingDelete) {
      cancelPendingPreview();
      updatePendingChrome(Boolean(fileInput?.files?.[0]));
      return;
    }
    if (!hasSavedAvatar) return;
    pendingDelete = true;
    clearMsg();
    showDeletedPreview();
    updatePendingChrome(false);
  });

  async function savePendingAvatarFromSelection(): Promise<void> {
    const f = fileInput?.files?.[0];
    if (f) {
      await apiPutAvatar(f);
      bumpAvatarRevision();
      avatarRefreshToken = bumpSettingsAvatarRefreshToken();
      if (fileInput) fileInput.value = '';
      pendingDelete = false;
      refreshImage();
      updatePendingChrome(false);
      return;
    }
    if (pendingDelete) {
      await apiDeleteAvatar();
      bumpAvatarRevision();
      avatarRefreshToken = bumpSettingsAvatarRefreshToken();
      pendingDelete = false;
      hasSavedAvatar = false;
      refreshImage();
      updatePendingChrome(false);
      showSettingsSaveToast('Successfully Deleted!');
    }
  }

  return {
    hasPendingAvatar: () => Boolean(fileInput?.files?.[0]) || pendingDelete,
    hasSavedAvatar: () => hasSavedAvatar,
    savePendingAvatarFromSelection,
    subscribeDirtyChange: (listener: () => void) => {
      dirtyListener = listener;
    },
    syncDefaultFallbackLabel: (label: string) => {
      currentFallbackLabel = label;
      if (!hasSavedAvatar) {
        if (fb) fb.textContent = initialFromLabel(currentFallbackLabel);
        if (!(fileInput?.files?.[0])) {
          refreshImage();
        }
      }
    }
  };
}

function bindDelete(): void {
  const btn = document.getElementById('acc-delete-btn') as HTMLButtonElement | null;
  const msg = document.getElementById('acc-delete-msg');
  btn?.addEventListener('click', async () => {
    const first = await appConfirm(
      'ディレクトリ上のアカウントを削除します。取り消しはできません。続行しますか？',
      { title: 'アカウント削除', okText: '続行', cancelText: 'やめる' }
    );
    if (!first) return;
    const second = await appConfirm('最終確認: 本当に削除しますか？', {
      title: '最終確認',
      okText: '削除する',
      cancelText: 'やめる'
    });
    if (!second) return;
    btn.disabled = true;
    clearSettingsMsg(msg);
    try {
      const res = await fetch('/api/DeleteMyAccount', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true })
      });
      const payload: unknown = await res.json().catch(() => ({}));
      const errText =
        typeof (payload as { error?: string })?.error === 'string'
          ? (payload as { error: string }).error
          : '';
      if (!res.ok) {
        const code = (payload as { code?: string })?.code;
        const ja =
          code === 'NOT_CONFIGURED'
            ? 'この環境ではアカウント削除 API が無効です（ENABLE_ACCOUNT_DELETE と Graph 設定が必要）。'
            : code === 'NO_SUBJECT'
              ? 'セッションからディレクトリユーザーを特定できませんでした。'
              : errText || `エラー (${res.status})`;
        setSettingsMsg(msg, ja, 'error');
        btn.disabled = false;
        return;
      }
      window.location.href = logoutHref();
    } catch {
      setSettingsMsg(msg, '通信に失敗しました。', 'error');
      btn.disabled = false;
    }
  });
}

type ProfileMeResponse = {
  ok?: boolean;
  showName?: string;
  screenName?: string;
  error?: string;
  code?: string;
};
async function apiProfileGet(): Promise<ProfileMeResponse | null> {
  const res = await fetch('/api/profile/me', { credentials: 'include', cache: 'no-store' });
  const data = (await res.json().catch(() => ({}))) as ProfileMeResponse;
  if (!res.ok) return { ...data, error: data.error || `HTTP ${res.status}` };
  return data;
}

async function resolveSettingsProfileCache(): Promise<SettingsProfileCache> {
  if (settingsProfileCache) return settingsProfileCache;
  const principal = await fetchMe();
  const roles = Array.isArray(principal?.userRoles) ? principal.userRoles : [];
  const isAuthed = roles.includes('authenticated') || roles.includes('administrator');
  if (!principal || !isAuthed) {
    settingsProfileCache = {
      principal,
      isAuthed: false,
      authorId: '',
      profile: null,
      screenName: '',
      avatarLabel: 'ユーザー',
      avatarRefreshToken: Date.now(),
      avatarExists: null
    };
    return settingsProfileCache;
  }
  const authorId = principalToStableAuthorId(principal);
  const profile = await apiProfileGet();
  const sn = typeof profile?.screenName === 'string' ? profile.screenName.trim() : '';
  const sh = typeof profile?.showName === 'string' ? profile.showName.trim() : '';
  const screenName = sn || sh;
  settingsProfileCache = {
    principal,
    isAuthed: true,
    authorId,
    profile,
    screenName,
    avatarLabel: sh || sn || 'ユーザー',
    avatarRefreshToken: Date.now(),
    avatarExists: null
  };
  return settingsProfileCache;
}

function bindScreenName(avatarBinding: AvatarBinding | null, initialScreenName: string) {
  const input = document.getElementById('acc-screen-name-input') as HTMLInputElement | null;
  const saveBtn = document.getElementById('acc-screen-save') as HTMLButtonElement | null;
  const msg = document.getElementById('acc-screen-msg');
  const DISPLAY_NAME_MAX_LEN = 24;
  let lastSavedScreenName = initialScreenName.trim();

  function showMsg(text: string, tone: SettingsMsgTone = 'neutral') {
    setSettingsMsg(msg, text, tone);
  }

  function clearMsg() {
    clearSettingsMsg(msg);
  }

  function applySaveDirtyState(): void {
    const displayDirty = (input?.value?.trim() ?? '') !== lastSavedScreenName;
    const avatarDirty = avatarBinding?.hasPendingAvatar() ?? false;
    const isDirty = displayDirty || avatarDirty;
    setSettingsUnsavedChanges(isDirty);
    saveBtn?.classList.toggle('is-dirty-pulse', isDirty);
  }

  input?.addEventListener('input', applySaveDirtyState);
  avatarBinding?.subscribeDirtyChange(applySaveDirtyState);
  applySaveDirtyState();

  function trimToDisplayNameMax(value: string): string {
    return Array.from(value).slice(0, DISPLAY_NAME_MAX_LEN).join('');
  }

  input?.addEventListener('beforeinput', (e: InputEvent) => {
    if (!input) return;
    if (e.inputType.startsWith('delete')) return;
    if (e.inputType === 'insertFromPaste') return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const inserted = e.data ?? '';
    const next = `${input.value.slice(0, start)}${inserted}${input.value.slice(end)}`;
    if (Array.from(next).length > DISPLAY_NAME_MAX_LEN) {
      e.preventDefault();
      showSettingsSaveToast('Display Nameは24文字以内にしてね', 'error');
    }
  });

  input?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (!input) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length !== 1) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const hasSelection = start !== end;
    const currentLen = Array.from(input.value).length;
    if (currentLen >= DISPLAY_NAME_MAX_LEN && !hasSelection) {
      showSettingsSaveToast('Display Nameは24文字以内にしてね', 'error');
    }
  });

  input?.addEventListener('paste', (e: ClipboardEvent) => {
    if (!input) return;
    const pasted = e.clipboardData?.getData('text') ?? '';
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = `${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`;
    if (Array.from(next).length <= DISPLAY_NAME_MAX_LEN) return;
    e.preventDefault();
    input.value = trimToDisplayNameMax(next);
    applySaveDirtyState();
    showSettingsSaveToast('Display Nameは24文字以内にしてね', 'error');
  });

  saveBtn?.addEventListener('click', async () => {
    const v = input?.value?.trim() ?? '';
    if (!v) {
      clearMsg();
      showSettingsSaveToast('1 文字以上入力してください。', 'error');
      return;
    }
    if (Array.from(v).length > DISPLAY_NAME_MAX_LEN) {
      clearMsg();
      showSettingsSaveToast('表示名は24文字以内で入力してください。', 'error');
      return;
    }
    clearMsg();
    saveBtn.disabled = true;
    try {
      const res = await fetch('/api/profile/me', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ screenName: v })
      });
      const data = (await res.json().catch(() => ({}))) as ProfileMeResponse;
      if (!res.ok) {
        const c = data.code;
        let detail = data.error || '保存に失敗しました。';
        if (c === 'IDP_SYNC_NOT_CONFIGURED') {
          detail = '表示名の IdP 同期用の設定が足りないよ。管理者に ENABLE_PROFILE_DISPLAYNAME_IDP_SYNC と Graph 用の環境変数を確認してもらってね。';
        } else if (c === 'NO_SUBJECT') {
          detail = 'ログイン情報からディレクトリ上のユーザーを特定できなかったよ。しばらくしてから再ログインして試してね。';
        } else if (c && typeof c === 'string' && c !== detail) {
          detail = `${detail} (${c})`;
        }
        clearMsg();
        showSettingsSaveToast(detail, 'error');
        return;
      }
      if (input && data.screenName !== undefined) input.value = data.screenName;
      avatarBinding?.syncDefaultFallbackLabel(input?.value?.trim() ?? v);
      if (avatarBinding?.hasPendingAvatar()) {
        await avatarBinding.savePendingAvatarFromSelection();
      }
      lastSavedScreenName = (input?.value?.trim() ?? v);
      syncSettingsCacheAfterScreenNameSave(lastSavedScreenName);
      clearMsg();
      showSettingsSaveToast('Successfully Saved!');
      applySaveDirtyState();
    } catch (e) {
      const err = e instanceof Error ? e.message : '通信に失敗しました。';
      clearMsg();
      showSettingsSaveToast(err, 'error');
    } finally {
      saveBtn.disabled = false;
    }
  });
}

/** app シェルがチャット outlet にプロフィール設定 HTML を注入したあとに呼ぶ */
export async function initSettingsShell(): Promise<void> {
  const root = document.getElementById('chat-settings');
  if (!root) return;
  if (root.dataset.settingsInited === '1') return;
  root.dataset.settingsInited = '1';

  setSettingsUnsavedChanges(false);
  const logoutLink = document.getElementById('acc-logout-link') as HTMLAnchorElement | null;
  if (logoutLink) logoutLink.href = logoutHref();
  // 初回表示時のレイアウトシフトを防ぐため、非同期 fetch 前に Save ボタン座標を確定させる
  alignSettingsSaveButtonToLegend();
  window.requestAnimationFrame(() => alignSettingsSaveButtonToLegend());
  if (!settingsSaveAlignResizeBound) {
    window.addEventListener('resize', alignSettingsSaveButtonToLegend);
    window.visualViewport?.addEventListener('resize', alignSettingsSaveButtonToLegend);
    settingsSaveAlignResizeBound = true;
  }

  const cache = await resolveSettingsProfileCache();

  const publicLabel = document.getElementById('acc-public-label');

  if (!cache.principal || !cache.isAuthed) {
    if (publicLabel) {
      publicLabel.innerHTML =
        '<span class="home-muted">表示名・アイコンの変更にはサインインが必要です。</span>';
    }
    return;
  }

  const input = document.getElementById('acc-screen-name-input') as HTMLInputElement | null;
  const initialScreenName = cache.screenName;
  if (input && initialScreenName) {
    input.value = initialScreenName;
  }

  let avatarBinding: AvatarBinding | null = null;
  if (cache.authorId) {
    avatarBinding = bindAvatar(
      cache.authorId,
      cache.avatarLabel,
      cache.avatarRefreshToken,
      cache.avatarExists
    );
  }
  bindScreenName(avatarBinding, initialScreenName);
  bindDelete();
}
